/**
 * formDeriveChainedReferenceHopIds
 *
 * The projections a config must REFUSE: those declaring a `rowReferenceProp`
 * column on a source table another projection fills. Consumers:
 * `createFormDeriveEngineConfig`, which drops them, and `validateFormDeriveDraft`,
 * which names them to the author — one spelling, because a second one accepts an
 * entry the engine drops and the drawer then reports a derive that never computes.
 * Why refuse rather than support: `reconcileFormDerives` builds its record fetch
 * set from the rows it PAGED, while `computeFormDerives` overlays each earlier
 * projection's planned rows onto its target table before a later entry reads it.
 * A chained reference cell therefore carries a docId no fetch pass ever saw, and
 * `requireRecord` throws before the pass writes anything — with `retry: true` on
 * both triggers, that is a permanent redelivery loop and no derive on the version
 * ever computes. Chained AGGREGATES stay supported: they read the overlay's cell
 * values and need no record.
 */

import type { FormDeriveEntry, FormDeriveId } from '../System.FormDerive.types';

function formDeriveChainedReferenceHopIds(
  entries: readonly FormDeriveEntry[],
): Set<FormDeriveId> {
  const projectionTargets = new Set(
    entries.flatMap((entry) => ('columns' in entry ? [entry.target.tableId] : [])),
  );

  return new Set(
    entries.flatMap((entry) =>
      'columns' in entry &&
      projectionTargets.has(entry.source.tableId) &&
      entry.columns.some(({ value }) => value.kind === 'rowReferenceProp')
        ? [entry.deriveId]
        : [],
    ),
  );
}

export { formDeriveChainedReferenceHopIds };
